import { QueryClient } from "@tanstack/react-query";

// Types
import { INotification, INotificationExtraInfo, IPaginatedNotificationsCache } from "@/Common/api/types/notifications";

/**
 * Handles the event of a new notification being created.
 */
export const onCreateNotificationHandler = (
	queryClient: QueryClient,
	userId: string,
	locationId: string,
	newNotification: INotification
) => {
	if (!newNotification) {
		return;
	}

	queryClient.setQueryData<INotificationExtraInfo>(
		["notificationsInfo", userId, locationId],
		(cachedNotificationInfo): INotificationExtraInfo | undefined => {
			if (!cachedNotificationInfo) {
				return cachedNotificationInfo;
			}

			return {
				...cachedNotificationInfo,
				totalCount: cachedNotificationInfo.totalCount + 1,
				newCount: cachedNotificationInfo.newCount + 1,
				currentFilterCount: cachedNotificationInfo.currentFilterCount + 1,
				unreadCount: cachedNotificationInfo.unreadCount + 1,
			};
		}
	);

	// Add the new Notification to all the cached `notificationList` queries that exist
	queryClient
		.getQueriesData({
			queryKey: ["notificationList", locationId, userId],
		})
		.forEach(([keys]) => {
			queryClient.setQueryData<IPaginatedNotificationsCache>(keys, (cachedData) =>
				insertNewNotification(cachedData, newNotification)
			);
		});
};

/**
 * Handles the update of the new notification count.
 */
export const onUpdateNewNotificationHandler = (queryClient: QueryClient, userId: string, locationId: string) => {
	queryClient.setQueryData<INotificationExtraInfo>(["notificationsInfo", userId, locationId], (cachedNotificationInfo) => {
		if (!cachedNotificationInfo) {
			return cachedNotificationInfo;
		}

		return {
			...cachedNotificationInfo,
			newCount: 0,
		};
	});

	// Update all the cached `notificationList` queries that exist
	queryClient
		.getQueriesData({
			queryKey: ["notificationList", locationId, userId],
		})
		.forEach(([key]) => {
			queryClient.setQueryData<IPaginatedNotificationsCache>(key, updateAllNewNotificationAttribute);
		});
};

/**
 * Handles the update of a notification.
 * Right now it is assuming the only update that can happen is the `read` attribute.
 */
export const onUpdateNotificationHandler = (
	queryClient: QueryClient,
	userId: string,
	locationId: string,
	updatedNotification: INotification
) => {
	if (!updatedNotification) {
		return;
	}

	queryClient.setQueryData<INotificationExtraInfo>(["notificationsInfo", userId, locationId], (cachedNotificationInfo) => {
		if (!cachedNotificationInfo) {
			return cachedNotificationInfo;
		}

		const modifyCount = (counter = 0, markAsRead = false) => Math.max(0, markAsRead ? counter - 1 : counter + 1);

		return {
			...cachedNotificationInfo,
			unreadCount: modifyCount(cachedNotificationInfo.unreadCount, updatedNotification.read),
		};
	});

	// Update the notificationList that has all the notifications
	queryClient.setQueryData<IPaginatedNotificationsCache>(["notificationList", locationId, userId, false], (cachedData) =>
		updateReadNotificationAttribute(cachedData, updatedNotification)
	);

	const refetchUnreadQuery = () => {
		queryClient.refetchQueries({ queryKey: ["notificationList", locationId, userId, true], exact: true });
	};

	if (updatedNotification.read) {
		queryClient.setQueryData<IPaginatedNotificationsCache>(["notificationList", locationId, userId, true], (cachedData) => {
			if (cachedData?.pageParams.at(-1)?.after === updatedNotification._id) {
				// The last page we have is pointing to the now read notification, we need to invalidate to get the correct new cursor
				refetchUnreadQuery();
				return undefined;
			}
			return deleteReadNotificationFromReadOnlyList(cachedData, updatedNotification._id);
		});
	} else {
		refetchUnreadQuery();
	}

	// Refetch the whole notificationList to ensure consistency
	queryClient.refetchQueries({ queryKey: ["notificationList", locationId, userId], exact: false });
};

/**
 * Handles the update of a notification.
 * Right now it is assuming the only update that can happen is the `read` attribute.
 */
export const onMarkAllAsReadHandler = (queryClient: QueryClient, userId: string, locationId: string) => {
	queryClient.setQueryData<INotificationExtraInfo>(["notificationsInfo", userId, locationId], (cachedNotificationInfo) => {
		if (!cachedNotificationInfo) {
			return cachedNotificationInfo;
		}

		return {
			...cachedNotificationInfo,
			unreadCount: 0,
		};
	});

	// Mark all saved Notifications as read
	queryClient.setQueryData<IPaginatedNotificationsCache>(["notificationList", locationId, userId, false], updateAllNotificationsAsRead);

	// There wont be any more unread notifications so we can reset this cache
	queryClient.refetchQueries({ queryKey: ["notificationList", locationId, userId, true], exact: true, type: "all" });
};

const insertNewNotification = (
	cachedData: IPaginatedNotificationsCache | undefined,
	newNotification: INotification
): IPaginatedNotificationsCache | undefined => {
	if (!cachedData) {
		return cachedData;
	}

	return {
		pages: [
			{
				...cachedData.pages[0],
				edges: [{ node: newNotification, cursor: newNotification._id }, ...cachedData.pages[0].edges],
			},
			...cachedData.pages.slice(1),
		],
		pageParams: [
			{
				...cachedData.pageParams[0],
				first: cachedData.pageParams[0].first + 1,
			},
			...cachedData.pageParams.slice(1),
		],
	};
};

const updateAllNewNotificationAttribute = (
	cachedData: IPaginatedNotificationsCache | undefined
): IPaginatedNotificationsCache | undefined => {
	if (!cachedData) {
		return cachedData;
	}

	return {
		...cachedData,
		pages: cachedData.pages.map((page) => ({
			...page,
			edges: page.edges.map((edge) => ({ ...edge, node: { ...edge.node, newNotification: false } })),
		})),
	};
};

const updateReadNotificationAttribute = (
	cachedData: IPaginatedNotificationsCache | undefined,
	updatedNotification: INotification
): IPaginatedNotificationsCache | undefined => {
	if (!cachedData || !updatedNotification) {
		return cachedData;
	}

	const { _id: updatedId, read } = updatedNotification;

	return {
		...cachedData,
		pages: cachedData.pages.map((page) => ({
			...page,
			edges: page.edges.map((edge) => ({ ...edge, node: edge.node._id === updatedId ? { ...edge.node, read } : edge.node })),
		})),
	};
};

const updateAllNotificationsAsRead = (cachedData: IPaginatedNotificationsCache | undefined): IPaginatedNotificationsCache | undefined => {
	if (!cachedData) {
		return cachedData;
	}

	return {
		...cachedData,
		pages: cachedData.pages.map((page) => ({
			...page,
			edges: page.edges.map((edge) => ({ ...edge, node: { ...edge.node, read: true } })),
		})),
	};
};

/**
 * Deletes a read notification from a read-only list.
 * @param notificationId The ID of the notification to delete.
 */
const deleteReadNotificationFromReadOnlyList = (
	cachedData: IPaginatedNotificationsCache | undefined,
	notificationId: string
): IPaginatedNotificationsCache | undefined => {
	if (!cachedData || !notificationId) {
		return cachedData;
	}

	const pageIndex = cachedData.pages.findIndex((page) => page.edges.find((edge) => edge.node._id === notificationId));

	if (pageIndex === -1) {
		return cachedData;
	}

	let newPageSize = cachedData.pageParams[pageIndex].first;

	if (newPageSize && cachedData.pages[pageIndex].edges.length) {
		newPageSize -= 1;
	}

	const alteredPage = {
		...cachedData.pages[pageIndex],
		edges: [...cachedData.pages[pageIndex].edges.filter((edge) => edge.node._id !== notificationId)],
	};
	const alteredPageParams = { ...cachedData.pageParams[pageIndex], first: newPageSize };

	return {
		...cachedData,
		pages: cachedData.pages.map((page, index) => (index === pageIndex ? alteredPage : page)),
		pageParams: cachedData.pageParams.map((pageParam, index) => (index === pageIndex ? alteredPageParams : pageParam)),
	};
};
